import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ColDef,
  GridOptions,
  GridReadyEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  TablePagination,
} from "@mui/material";
import { RootState, AppDispatch } from "../redux/Store";
import { addQuery, QueryData } from "../redux/actions";
import useCustomStyles from "../hooks/CustomStylesHook";
import { useTheme } from "@emotion/react";
import { EXPLORE_FILES } from "../constants/constants";
import AddIcon from "@mui/icons-material/Add";
import {
  deleteEditedEmbeddings,
  editResponse,
  EditResponsePayload,
  fetchUserQueries,
} from "../services/services";
import CustomHeader from "./CustomHeaderRenderer";
import { RewriteActionCellRenderer } from "./ActionCellRenderer";

interface EmailData {
  query: string;
  response: string;
  query_hash: string;
  admin_username?: string;
  course_name?: string;
}

const styles = (theme: any) => ({
  container: {
    padding: "20px",
    minHeight: "90vh",
    justifyContent: "center",
    alignItems: "center",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 100%)`,
  },
  tableContainer: {
    marginTop: "20px",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    overflow: "scroll",
  },
  tableHeader: {
    backgroundColor: "#235678",
    color: theme.palette.primary.contrastText,
  },
  tableCellHeader: {
    fontWeight: "bold",
    textAlign: "center",
    fontSize: "16px",
    color: theme.palette.primary.contrastText,
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    textAlign: "center",
    padding: "10px",
    alignItems: "center",
  },
  actionButton: {
    display: "flex",
    justifyContent: "center",
    "& > *": {
      margin: "0 5px",
    },
  },
  titleContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "5px 0px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#ffffff",
    marginBottom: "20px",
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
  },
  addButton: {
    color: "white",
    padding: "7px 10px 6px 5px",
    backgroundColor: "#1B4F72",
    borderRadius: "10px",
  },
  pdfPreviewFrame: {
    minWidth: "700px",
    minHeight: "500px",
    border: "none",
  },
  nofilesText: {
    textAlign: "center",
  },
  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    zIndex: 9998,
  },
  docLoadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  fileUploadDialogContent: {
    padding: "0px 24px !important",
  },
  fileName: {
    whiteSpace: "nowrap",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actionIcons: {
    cursor: "pointer",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 20px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
    marginBottom: "20px",
  },

  searchField: {
    width: "300px",
    backgroundColor: theme.palette.background.default,
    borderRadius: "5px",
    "& .MuiInputBase-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "5px",
    },
  },
  dialogTextField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  pagination: {
    color: "white",
    "& .MuiIconButton-root": { color: "white", textAlign: "left" },
    "& .MuiSvgIcon-root-MuiSelect-icon": { color: "white" },

    "& .MuiSelect-icon": { color: "white" },
  },
  editDialog: {
    "& .MuiDialog-paper": {
      width: "600px",
      maxWidth: "90vw",
    },
  },
  editDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "24px",
  },
  agGridContainer: {
    height: "70vh",
    width: "100%",
    backgroundColor: "white",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    border: "none",
    "& .ag-header": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    "& .ag-header-cell": {
      fontWeight: "bold",
      fontSize: "16px",
      textAlign: "center",
      padding: "10px",
      color: "white",
    },
    "& .ag-cell": {
      textAlign: "center",
    },
    "& .ag-cell-label-container": {
      textAlign: "center",
      padding: "0px",
    },
    "& .ag-row-even": {
      backgroundColor: theme.palette.background.default,
    },
    "& .ag-row-odd": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .ag-row:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .ag-row-selected": {
      backgroundColor: theme.palette.action.selected,
    },
    "& .ag-icon, .ag-filter-icon": {
      color: theme.palette.primary.main,
    },
    "& .ag-paging-panel": {
      backgroundColor: theme.palette.background.default,
      padding: "10px",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& .ag-header-cell-resize": {
      backgroundColor: theme.palette.background.default,
    },
  },
});

const ExplorePage: React.FC = () => {
  const theme = useTheme();
  const classes = useCustomStyles(styles, theme);
  const queries = useSelector((state: RootState) => state.fileReducer.queries);
  const dispatch = useDispatch<AppDispatch>();

  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [filteredQueries, setFilteredQueries] = useState(queries);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [editingData, setEditingData] = useState<EmailData>({
    query: "",
    response: "",
    query_hash: "",
    course_name: "",
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredQueries?.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredQueries, page, rowsPerPage]);

  const loadQueries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchUserQueries("");
      const formattedQueries = data?.response?.edited_embeddings?.map(
        (query: {
          content_hash: string;
          query: string;
          edited_response: string;
        }) => ({
          query_hash: query?.content_hash,
          query: query?.query,
          response: query?.edited_response,
        })
      );
      if (formattedQueries) {
        dispatch(addQuery(formattedQueries));
        setFilteredQueries(formattedQueries);
      }
    } catch (error) {
      console.error(EXPLORE_FILES?.FILE_LOAD_FAIL, error);
      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.FILE_LOAD_FAIL,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const handleSubmitEdit = async (isAdd: boolean) => {
    setIsLoading(true);

    try {
      if (isAdd) {
        await handleDeleteQuery(editingData);
      }
      const payload: EditResponsePayload = {
        question: editingData?.query,
        edit_response: editingData?.response,
        admin_username: editingData?.admin_username || "",
        course_name: editingData?.course_name || "",
      };

      await editResponse(payload);
      await loadQueries();

      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.QUERY_UPDATED,
        severity: "success",
      });

      handleCloseEdit();
    } catch (error) {
      console.error("Error updating query:", error);
      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.UPDATE_ERROR,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilter = useCallback(
    (filterValue: string) => {
      const lowerCaseFilter = filterValue?.toLowerCase();
      const filtered: QueryData[] = queries?.filter((query) =>
        Object.values(query)?.some(
          (value) =>
            typeof value === "string" &&
            value?.toLowerCase()?.includes(lowerCaseFilter)
        )
      );

      setFilteredQueries(filtered);
    },
    [queries]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    applyFilter(e.target.value);
  };

  const handleDeleteQuery = useCallback(
    async (data: EmailData) => {
      try {
        setIsLoading(true);
        await deleteEditedEmbeddings(data?.query_hash);
        await loadQueries();
        setSnackbar({
          open: true,
          message: EXPLORE_FILES?.FILE_DELETED,
          severity: "success",
        });
      } catch (error) {
        console.error(EXPLORE_FILES?.DELETE_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadQueries]
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Query",
        field: "query",
        sortable: true,
        width: 200,
        filter: true,
        headerComponent: CustomHeader,
        tooltipField: "query",
        tooltipComponentParams: { color: "#444444" },
        cellStyle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Response",
        field: "response",
        width: 300,
        sortable: true,
        filter: true,
        headerComponent: CustomHeader,
        tooltipField: "response",
        tooltipComponentParams: { color: "#444444" },
        cellStyle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        resizable: false,
        width: 200,
        cellRenderer: "actionCellRenderer",
        sortable: false,
      },
      {
        headerName: "Query Hash",
        field: "query_hash",
        hide: true,
      },
    ],
    []
  );

  const handleEditClick = (data: EmailData, isAdd: boolean) => {
    if (isAdd) {
      setIsAdd(true);
      setEditingData({
        query: "",
        response: "",
        query_hash: "",
        admin_username: "",
        course_name: "",
      });
    } else {
      setEditingData({
        ...data,
        query_hash: data.query_hash,
        admin_username: data.admin_username || "",
        course_name: data.course_name || "",
      });
    }
    setOpenEdit(true);
  };

  const gridOptions: GridOptions<QueryData> = {
    columnDefs,
    rowData: paginatedData,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      filter: true,
      sortable: true,
      resizable: true,
    },
    components: {
      actionCellRenderer: RewriteActionCellRenderer,
    },
    context: {
      onEdit: (data: EmailData) => handleEditClick(data, false),
      onDelete: (data: EmailData) => handleDeleteQuery(data),
      classes,
    },
    pagination: true,
    paginationPageSize: rowsPerPage,
    rowModelType: "clientSide",
    suppressCellFocus: true,
    onGridReady: (params: GridReadyEvent) => {
      params?.api?.sizeColumnsToFit();
    },
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  return (
    <div className={classes?.container}>
      {isLoading && (
        <CircularProgress id="loading-circular-progress" size={60} />
      )}
      {openEdit ? (
        <Dialog
          open={openEdit}
          onClose={handleCloseEdit}
          maxWidth={false}
          className={classes?.editDialog}
        >
          <DialogTitle>{EXPLORE_FILES?.DOCUMENT_PREVIEW}</DialogTitle>
          <DialogContent className={classes?.editDialogContent}>
            <TextField
              fullWidth
              label="Query"
              value={editingData?.query || ""}
              multiline
              rows={2}
              onChange={(e) =>
                setEditingData((prev) =>
                  prev ? { ...prev, query: e.target.value } : prev
                )
              }
              margin="normal"
              className={classes?.dialogTextField}
            />
            <TextField
              fullWidth
              label="Response"
              value={editingData?.response || ""}
              multiline
              rows={4}
              onChange={(e) =>
                setEditingData((prev) =>
                  prev ? { ...prev, response: e.target.value } : prev
                )
              }
              className={classes?.dialogTextField}
            />
            <TextField
              fullWidth
              label="Admin Username"
              value={editingData?.admin_username || ""}
              onChange={(e) =>
                setEditingData((prev) =>
                  prev ? { ...prev, admin_username: e.target.value } : prev
                )
              }
              className={classes?.dialogTextField}
            />
            <TextField
              fullWidth
              label="Course Name "
              value={editingData?.course_name || ""}
              onChange={(e) =>
                setEditingData((prev) =>
                  prev ? { ...prev, course_name: e.target.value } : prev
                )
              }
              className={classes?.dialogTextField}
            />
          </DialogContent>
          <DialogActions>
            <Button
              id="close-document-preview-icon"
              onClick={handleCloseEdit}
              color="primary"
            >
              {EXPLORE_FILES?.CLOSE}
            </Button>
            <Button
              id="submit-edit-button"
              onClick={() => handleSubmitEdit(isAdd)}
              color="primary"
              variant="contained"
            >
              {EXPLORE_FILES?.SUBMIT}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      <div className={classes?.titleContainer}>
        <TextField
          id="searchField"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          className={classes?.searchField}
          placeholder="Search"
        />
        <Button
          id="add-file-button"
          className={classes?.addButton}
          onClick={() =>
            handleEditClick({ response: "", query: "", query_hash: "" }, true)
          }
        >
          <AddIcon />
          {EXPLORE_FILES?.ADD_FILE}
        </Button>
      </div>
      <div className={classes?.agGridContainer}>
        <AgGridReact
          {...gridOptions}
          className={`ag-theme-alpine ${classes?.agGridContainer}`}
          modules={[ClientSideRowModelModule]}
          pagination={true}
          paginationPageSize={rowsPerPage}
        />

        <TablePagination
          count={filteredQueries?.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 30, 40]}
          size="small"
          className={classes?.pagination}
        />
      </div>
      <Snackbar
        id="explore-files-snackbar"
        open={snackbar?.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          id="explore-files-snackbar-alert"
          onClose={handleSnackbarClose}
          severity={snackbar?.severity}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ExplorePage;
